import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import cx from 'classnames'

import { ADORNMENTS } from 'config/constants'

import DocumentImportCommonFields from './document-import-common-fields'
import DocumentImportRowCode from './document-import-row-code'
import '../invoice/components/invoiceDetails/components/products/components/add_form/add_form.scss'

const DECIMAL_PRODUCT_FIELDS: (keyof CalculationDocumentDraftProduct)[] = [
  'unit_price',
  'price',
  'length',
  'width',
  'height',
  'net_weight_cartoon',
  'gross_weight_cartoon',
  'net_weight_manual',
  'gross_weight_manual',
  'volume_manual',
]

const INTEGER_PRODUCT_FIELDS: (keyof CalculationDocumentDraftProduct)[] = [
  'pieces',
  'places_manual',
  'pieces_in_cartoon',
]

export const inferCommonPackageMode = (row: CalculationDocumentDraftProduct): boolean => {
  if (row.length && row.width && row.height) {
    return false
  }
  if (row.volume_manual || row.net_weight_manual || row.gross_weight_manual) {
    return true
  }
  if (row.net_weight_cartoon || row.gross_weight_cartoon) {
    return false
  }
  return false
}

const volumeFromDimensionsCm = (
  length?: number | null,
  width?: number | null,
  height?: number | null,
): number | null => {
  if (!length || !width || !height) {
    return null
  }
  const volume = (Number(length) * Number(width) * Number(height)) / 1_000_000
  return Number.isFinite(volume) ? volume : null
}

const parseDecimal = (value: string): number | null => {
  if (value === '') {
    return null
  }
  const normalized = value.replace(',', '.')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

const parseInteger = (value: string): number | null => {
  if (value === '') {
    return null
  }
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : null
}

type Props = {
  index: number
  row: CalculationDocumentDraftProduct
  readiness: CalculationDocumentDraftReadiness
  showPackagingRequired: boolean
  onChange: (index: number, field: keyof CalculationDocumentDraftProduct, value: string) => void
  onPatch: (index: number, patch: Partial<CalculationDocumentDraftProduct>) => void
  onPackagingModeChange: (index: number, mode: 'common' | 'per_package') => void
  onPriceModeChange: (index: number, mode: 'unit_price' | 'price') => void
}

const DocumentImportProductEditor = ({
  index,
  row,
  readiness,
  showPackagingRequired,
  onChange,
  onPatch,
  onPackagingModeChange,
  onPriceModeChange,
}: Props) => {
  const { t } = useTranslation()
  const isCommonPackage = row.packaging_mode
    ? row.packaging_mode === 'common'
    : inferCommonPackageMode(row)

  const productReadiness = useMemo(
    () => readiness.products.find((item) => item.index === index),
    [ index, readiness.products ],
  )

  const isMissing = (field: string) => Boolean(productReadiness?.missing_fields.includes(field))
  const packagingError = showPackagingRequired && isMissing('packaging')
  const computedVolume = volumeFromDimensionsCm(row.length, row.width, row.height)

  const decimalField = (
    field: keyof CalculationDocumentDraftProduct,
    label: string,
    adornment?: React.ReactNode,
    decimals?: number,
  ) => (
    <TextField
      key={field}
      className={`add-product__${field}`}
      size='small'
      fullWidth
      label={label}
      InputLabelProps={{ shrink: true }}
      value={row[field] ?? ''}
      error={packagingError && DECIMAL_PRODUCT_FIELDS.includes(field)}
      onChange={(event) => onChange(index, field, event.target.value)}
      inputProps={decimals ? { step: decimals === 3 ? '0.001' : '0.01' } : undefined}
      InputProps={adornment ? { endAdornment: adornment } : undefined}
    />
  )

  return (
    <div className='document-import__product add-product'>
      <Typography variant='subtitle2' sx={{ mb: 2 }}>
        {t('calc__text__documentImport__productLine', { number: index + 1 })}
      </Typography>
      <div
        className={cx({
          'add-product__form': true,
          'add-product__form_isCommon': isCommonPackage,
          'document-import__product-form': true,
        })}
      >
        <TextField
          className='add-product__name'
          size='small'
          fullWidth
          label={t('calc__text__packaging__nameItem')}
          InputLabelProps={{ shrink: true }}
          value={row.name || ''}
          error={isMissing('name')}
          onChange={(event) => onChange(index, 'name', event.target.value)}
        />
        <DocumentImportRowCode
          countryCode={row.country_code || ''}
          hsCode={row.code || ''}
          countryError={isMissing('country_code')}
          codeError={isMissing('code')}
          onCountryChange={(code) => onChange(index, 'country_code', code)}
          onCodeChange={(code) => onChange(index, 'code', code)}
        />
        <DocumentImportCommonFields
          index={index}
          row={row}
          onChange={onChange}
          onPatch={onPatch}
          onPriceModeChange={onPriceModeChange}
          fieldError={isMissing}
        />
        <FormControlLabel
          className='add-product__common_package'
          control={
            <Switch
              color='secondary'
              checked={isCommonPackage}
              onChange={(_event, checked) => {
                onPackagingModeChange(index, checked ? 'common' : 'per_package')
              }}
            />
          }
          label={t('common__text__totalDimensionsOfTheLoad')}
          sx={{ marginLeft: 0 }}
        />
        {packagingError && (
          <Typography variant='caption' color='error' className='document-import__packaging-hint'>
            {t('calc__text__documentImport__packagingRequired')}
          </Typography>
        )}
        {isCommonPackage ? (
          <div className='add-product__row document-import__packaging-block'>
            {decimalField('volume_manual', t('common__text__totalVolume'), ADORNMENTS.VOLUME, 3)}
            {decimalField('net_weight_manual', t('common__text__totalNetWeight'), ADORNMENTS.WEIGHT)}
            {decimalField('gross_weight_manual', t('common__text__totalGrossWeight'), ADORNMENTS.WEIGHT)}
          </div>
        ) : (
          <>
            <div className='add-product__row document-import__packaging-block'>
              {decimalField('length', t('calc__text__packaging__lengthOfPackage'), ADORNMENTS.SIZE)}
              {decimalField('width', t('calc__text__packaging__widthOfPackage'), ADORNMENTS.SIZE)}
              {decimalField('height', t('calc__text__packaging__heightOfPackage'), ADORNMENTS.SIZE)}
            </div>
            {decimalField('net_weight_cartoon', t('common__text__netWeightPackaging'), ADORNMENTS.WEIGHT)}
            {decimalField('gross_weight_cartoon', t('common__text__grossPackagingWeight'), ADORNMENTS.WEIGHT)}
            {computedVolume != null && (
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {t('calc__text__documentImport__computedVolume', { value: computedVolume.toFixed(3) })}
              </Typography>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export const parseProductFieldValue = (
  field: keyof CalculationDocumentDraftProduct,
  value: string,
): string | number | null => {
  if (INTEGER_PRODUCT_FIELDS.includes(field)) {
    return parseInteger(value)
  }
  if (DECIMAL_PRODUCT_FIELDS.includes(field)) {
    return parseDecimal(value)
  }
  return value
}

export default DocumentImportProductEditor
